/**
 * The single source of truth for which no-backend runtime mode the app is in.
 *
 * web-app has two independent ways to run without the deployed backend, owned
 * by two different audiences:
 *
 * - **local-dev** (`NEXT_PUBLIC_RUNTIME_ENVIRONMENT=local`) — platform/backend
 *   engineers running the `workspace/local-dev` docker stack against a real
 *   jasper-api on localhost:8082, with a JWT minted by `grpcurl`.
 *   Entry point `pnpm local-dev`.
 * - **mock mode** (`NEXT_PUBLIC_MOCK_MODE=1`) — the applications team and cloud
 *   agents, running entirely off the committed SDL with no backend at all.
 *   Entry point `pnpm mock`.
 *
 * The two must never bleed into each other. Every consumer imports the
 * predicates below rather than re-reading `process.env`, so the branches in
 * `proxy.ts`, `lookupTenant.ts`, `lookupFeature.ts` and `session.tsx`
 * cannot drift apart over time. `runtimeMode.test.ts` enforces the invariants.
 *
 * Note this module owns the mode *triggers* only. The per-mode flag-file
 * pointers (`LOCAL_FEATURE_FLAGS_FILE`, `MOCK_FEATURE_FLAGS_FILE`) are read by
 * the server-only modules that consume them, since those need `fs` and this
 * module must stay isomorphic for the proxy and client bundles.
 */

/** Env vars that belong to local-dev. Mock mode must never read or write these. */
export const LOCAL_DEV_ENV_VARS = [
	"NEXT_PUBLIC_RUNTIME_ENVIRONMENT",
	"LOCAL_FEATURE_FLAGS_FILE",
	"NEXT_PUBLIC_LOCAL_DEV_SESSION_TOKEN",
	"NEXT_PUBLIC_LOCAL_DEV_SESSION_EXPIRES",
	"NEXT_PUBLIC_LOCAL_DEV_TENANT_ID",
] as const;

/** Env vars that belong to mock mode. local-dev must never read or write these. */
export const MOCK_MODE_ENV_VARS = [
	"NEXT_PUBLIC_MOCK_MODE",
	"MOCK_FEATURE_FLAGS_FILE",
	"MOCK_SCENARIO",
	"MOCK_NOW",
	"MOCK_UI_THEME",
	"NEXT_PUBLIC_MOCK_SIGNED_OUT",
	"NEXT_PUBLIC_E2E",
] as const;

/**
 * Files whose contents local-dev owns exclusively. `sync-local-feature-flags.mjs`
 * writes `.env.development.local` and `pnpm dev` deletes it, so mock mode must
 * deliver its configuration through process env instead of writing here.
 */
export const LOCAL_DEV_OWNED_FILES = [".env.development.local"] as const;

// Read as literal `process.env.X` expressions, never dynamically: Next.js only
// inlines NEXT_PUBLIC_* vars into client bundles when it can see the literal
// property access at build time.
const readLocalDevTrigger = () =>
	process.env.NEXT_PUBLIC_RUNTIME_ENVIRONMENT === "local";
const readMockModeTrigger = () => process.env.NEXT_PUBLIC_MOCK_MODE === "1";
const readE2ETrigger = () => process.env.NEXT_PUBLIC_E2E === "1";

function assertModesAreExclusive(): void {
	if (readLocalDevTrigger() && readMockModeTrigger()) {
		throw new Error(
			"Both local-dev and mock mode are enabled (NEXT_PUBLIC_RUNTIME_ENVIRONMENT=local and " +
				"NEXT_PUBLIC_MOCK_MODE=1). They are mutually exclusive: local-dev talks to a real " +
				"jasper-api on localhost:8082, mock mode talks to no backend at all. Pick one — run " +
				"`pnpm local-dev` or `pnpm mock`, not both. A leftover .env.development.local from a " +
				"previous `pnpm local-dev` is the usual cause; delete it or run `pnpm dev` once to clean it.",
		);
	}
}

/**
 * True when running against the local-dev docker stack. Unchanged in meaning
 * from the `NEXT_PUBLIC_RUNTIME_ENVIRONMENT === "local"` checks it replaces.
 */
export function isLocalDevMode(): boolean {
	assertModesAreExclusive();
	return readLocalDevTrigger();
}

/** True when running fully mocked off the committed SDL, with no backend. */
export function isMockMode(): boolean {
	assertModesAreExclusive();
	return readMockModeTrigger();
}

/**
 * True in the mock-mode build the Playwright suite in `e2e/` runs on
 * (`run-mock.mjs build --e2e`). Inlined at build time, so the server render and
 * the browser agree. Only counts in mock mode.
 */
export function isE2EBuild(): boolean {
	return isMockMode() && readE2ETrigger();
}
